import {
    Badge,
    Banner,
    Button,
    CodeInput,
    ConfirmDialog,
    DateTime,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorState,
    Field,
    PageHeader,
    SecretReveal,
    Section,
    Skeleton,
} from '@bemor/web-kit/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { errorCopy } from '@/lib/auth';
import { TimeZoneSection } from '@/pages/settings/time-zone-section';
import {
    authCall,
    type AuthError,
    type AuthResult,
    type RecoveryCodes,
    type RevokedCount,
    type SessionRow,
} from '@bemor/web-kit/staff-auth';

const SESSIONS_KEY = ['auth', 'sessions'] as const;

/** Throws the error item so react-query holds it; the screens read it back with `errorCopy`. */
async function must<T>(call: Promise<AuthResult<T>>): Promise<T> {
    const r = await call;
    if (!r.ok) throw r.error;
    return r.data;
}

const asAuthError = (e: unknown): AuthError =>
    e && typeof e === 'object' && 'key' in e
        ? (e as AuthError)
        : { key: 'network', message: String(e) };

/** New recovery codes: a current code first; the new set is shown once and the old one stops working. */
function RecoveryCodesDialog({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) {
    const [code, setCode] = useState('');
    const [wrong, setWrong] = useState(false);
    const [codes, setCodes] = useState<readonly string[] | null>(null);
    const [error, setError] = useState<AuthError | null>(null);
    const make = useMutation({
        mutationFn: (value: string) =>
            must(
                authCall<RecoveryCodes>(
                    'POST',
                    '/auth/two-factor/recovery-codes',
                    { code: value },
                ),
            ),
    });
    const close = () => {
        setCode('');
        setWrong(false);
        setCodes(null);
        setError(null);
        onClose();
    };
    const run = (value: string) => {
        if (make.isPending) return;
        setError(null);
        make.mutate(value, {
            onSuccess: (data) => setCodes(data.recovery_codes),
            onError: (e) => {
                const err = asAuthError(e);
                setError(err);
                if (err.key === 'invalid_code') {
                    setWrong(true);
                    setCode('');
                }
            },
        });
    };
    return (
        <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
            <DialogContent data-testid="recovery-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {codes
                            ? 'Save your new recovery codes'
                            : 'Make new recovery codes'}
                    </DialogTitle>
                    <DialogDescription>
                        {codes
                            ? 'Each code signs you in once if you lose your phone. The old codes no longer work.'
                            : 'Enter the 6-digit code from your authenticator app. The new set replaces the old one.'}
                    </DialogDescription>
                </DialogHeader>
                {codes ? (
                    <SecretReveal secret={[...codes]} label="Recovery codes" />
                ) : (
                    <Field
                        label="Code"
                        htmlFor="recovery-totp"
                        error={error ? errorCopy(error) : undefined}
                        reserve
                    >
                        <CodeInput
                            id="recovery-totp"
                            label="Authenticator code"
                            autoFocus
                            value={code}
                            error={wrong}
                            disabled={make.isPending}
                            onChange={(v) => {
                                setCode(v);
                                if (v) setWrong(false);
                            }}
                            onComplete={run}
                        />
                    </Field>
                )}
                <DialogFooter>
                    {codes ? (
                        <Button variant="brand" onClick={close}>
                            I saved them
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outline"
                                onClick={close}
                                disabled={make.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="brand"
                                loading={make.isPending}
                                disabled={code.length !== 6}
                                onClick={() => run(code)}
                            >
                                Make new codes
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Two-step sign-in: always on for staff (Identity refuses to turn it off with `totp_required`), so no switch. */
function TwoStepSection() {
    const [open, setOpen] = useState(false);
    return (
        <Section
            title="Two-step sign-in"
            action={
                <Badge variant="success" data-testid="totp-status">
                    On
                </Badge>
            }
            testId="security-two-step"
        >
            <div className="flex flex-col gap-4">
                <p className="text-fg-2 m-0 text-sm">
                    Every sign-in asks for a 6-digit code from your
                    authenticator app. Admin accounts always need it. Lost your
                    phone? Sign in with a recovery code, or ask a staff manager
                    to reset your authenticator.
                </p>
                <div>
                    <Button variant="secondary" onClick={() => setOpen(true)}>
                        Make new recovery codes
                    </Button>
                </div>
            </div>
            <RecoveryCodesDialog open={open} onClose={() => setOpen(false)} />
        </Section>
    );
}

/** Sessions: this browser marked; end one, or every other. */
function SessionsSection() {
    const client = useQueryClient();
    const sessions = useQuery({
        queryKey: SESSIONS_KEY,
        queryFn: () => must(authCall<SessionRow[]>('GET', '/auth/sessions')),
    });
    const [ending, setEnding] = useState<SessionRow | null>(null);
    const [endAll, setEndAll] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const refresh = () => client.invalidateQueries({ queryKey: SESSIONS_KEY });
    const endOne = useMutation({
        mutationFn: (s: SessionRow) =>
            must(
                authCall(
                    'DELETE',
                    `/auth/sessions/${encodeURIComponent(s.id)}`,
                ),
            ),
        onSuccess: () => refresh(),
    });
    const endOthers = useMutation({
        mutationFn: () =>
            must(authCall<RevokedCount>('DELETE', '/auth/sessions')),
        onSuccess: () => refresh(),
    });
    const rows = sessions.data ?? [];
    const others = rows.filter((s) => !s.current);
    return (
        <Section
            title="Sessions"
            description="Where your account is signed in now."
            list
            testId="security-sessions"
            action={
                others.length > 0 ? (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEndAll(true)}
                    >
                        End all other sessions
                    </Button>
                ) : null
            }
        >
            {notice ? (
                <div className="px-5 pt-4">
                    <Banner tone="success">{notice}</Banner>
                </div>
            ) : null}
            {sessions.isPending ? (
                <ul className="m-0 list-none p-0" aria-busy="true">
                    {[0, 1, 2].map((i) => (
                        <li
                            key={i}
                            className="border-border-1 flex flex-col gap-2 px-5 py-4 not-first:border-t"
                        >
                            <Skeleton className="h-5 w-48" />
                            <Skeleton className="h-4 w-64" />
                        </li>
                    ))}
                </ul>
            ) : sessions.isError ? (
                <ErrorState
                    message={errorCopy(asAuthError(sessions.error))}
                    correlationId={asAuthError(sessions.error).correlationId}
                    onRetry={() => void sessions.refetch()}
                />
            ) : (
                <ul className="m-0 list-none p-0" data-testid="session-rows">
                    {rows.map((s) => (
                        <li
                            key={s.id}
                            data-testid="session-row"
                            data-session-id={s.id}
                            data-current={s.current ? 'true' : 'false'}
                            className="border-border-1 flex flex-wrap items-center justify-between gap-3 px-5 py-4 not-first:border-t"
                        >
                            <div className="flex min-w-0 flex-col gap-1">
                                <span className="text-fg-1 flex flex-wrap items-center gap-2 text-sm font-medium">
                                    {s.label || 'Unknown browser'}
                                    {s.current ? (
                                        <Badge variant="info">
                                            This browser
                                        </Badge>
                                    ) : null}
                                </span>
                                <span className="text-fg-2 text-xs">
                                    {s.ip ? (
                                        <span className="font-mono">
                                            {s.ip}
                                        </span>
                                    ) : (
                                        'Address unknown'
                                    )}
                                    {', last active '}
                                    <DateTime value={s.last_seen_at} />
                                    {', signed in '}
                                    <DateTime value={s.created_at} />
                                </span>
                            </div>
                            {s.current ? null : (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEnding(s)}
                                    aria-label={`End the session on ${s.label || 'an unknown browser'}`}
                                >
                                    End
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            <ConfirmDialog
                open={ending !== null}
                onCancel={() => {
                    endOne.reset();
                    setEnding(null);
                }}
                title="End this session?"
                body={`${ending?.label || 'That browser'} is signed out at once. Its next click asks for Google and the code again.`}
                confirmLabel="End session"
                destructive
                loading={endOne.isPending}
                error={
                    endOne.error
                        ? errorCopy(asAuthError(endOne.error))
                        : undefined
                }
                onConfirm={() =>
                    ending &&
                    endOne.mutate(ending, {
                        onSuccess: () => {
                            setNotice('Session ended.');
                            setEnding(null);
                        },
                    })
                }
            />
            <ConfirmDialog
                open={endAll}
                onCancel={() => {
                    endOthers.reset();
                    setEndAll(false);
                }}
                title="End all other sessions?"
                body="Every browser except this one is signed out at once."
                confirmLabel="End other sessions"
                destructive
                loading={endOthers.isPending}
                error={
                    endOthers.error
                        ? errorCopy(asAuthError(endOthers.error))
                        : undefined
                }
                onConfirm={() =>
                    endOthers.mutate(undefined, {
                        onSuccess: (r) => {
                            setNotice(
                                r.revoked === 1
                                    ? 'Ended 1 other session.'
                                    : `Ended ${r.revoked} other sessions.`,
                            );
                            setEndAll(false);
                        },
                    })
                }
            />
        </Section>
    );
}

/**
 * `/settings/security` (missing design, ROUND-7-DRAFT section 5, built from the kit and the 0.4.0 account screens):
 * the person's own time zone (BM-143), two-step sign-in (no turn-off for staff), new recovery codes, sessions. Staff have no trusted devices. BM-45: staff sign
 * in with Google only, so there is no password to change here.
 */
export function SecurityPage() {
    return (
        <div className="flex flex-col gap-6" data-testid="security-page">
            <PageHeader
                title="Security"
                meta="How you sign in to Admin, and where."
            />
            <TimeZoneSection />
            <TwoStepSection />
            <SessionsSection />
        </div>
    );
}
